import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { useRoleNavigate } from "@/hooks/useRoleNavigate";
import { RoutesLink } from "@/utility/RouteLinks";
import { DownloadCloud, X, CheckSquare, Eye } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { IProduct, IProductResponse } from "@/interfaces/Product/product.interface";
import CommonTable, { Action, TableColumn } from "@/components/common/CommonTable";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { useFilter } from "@/hooks/useFilter";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { GetFilterUrl, formatRand, showToastNotification } from "@/utility/helper";
import Pagination from "@/components/common/Pagination";
import { useActiveRole } from "@/hooks/useActiveRole";
import { RootState, useAppSelector } from "@/redux/store";
import { TableFilters } from "@/components/common/TableFilters";
import { CommonSelect } from "@/components/common/CommonSelect";
import { TableItemIdentity } from "@/components/common/TableItemIdentity";
import ConfirmationModal from "@/components/common/ConfirmationModal";

export const ImportProducts = () => {
  const navigate = useRoleNavigate();
  const [products, setProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [isImportLoading, setIsImportLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { user } = useAppSelector((state: RootState) => state.user);

  const { filters, debouncedFilters, updateFilter, handlePageChange } = useFilter<IFilter>({
    search: '',
    isGlobal: '1',
    sort: 'newest',
    page: 1,
    limit: 10,
  });

  const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
    limit: 10,
    page: 1,
    total: 0,
    totalPages: 0,
  });

  const lastParamsRef = useRef<string>('');
  const isInitialSyncDone = useRef(false);

  const fetchProducts = async (syncStatus = false) => {
    const currentParams = JSON.stringify(debouncedFilters);
    if (!syncStatus && currentParams === lastParamsRef.current) return;
    lastParamsRef.current = currentParams;

    setLoading(true);
    try {
      const queryParams = GetFilterUrl(debouncedFilters);
      const promises: [Promise<IProductResponse>, Promise<IProductResponse> | null] = [
        callApi<IProductResponse>(`${API_URL + API_ENDPOINT.products}/global-products?${queryParams}`, { method: 'GET' }),
        syncStatus
          ? callApi<IProductResponse>(
            `${API_URL + API_ENDPOINT.products}?userId=${user?.id}&limit=1000`,
            { method: 'GET' }
          )
          : null
      ];

      const [response, statusResponse] = await Promise.all(promises);
      setProducts(response?.data?.products || []);
      setPageMetadata(response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 });

      if (statusResponse) {
        const storeProducts = statusResponse?.data?.products || [];
        const ids = new Set(
          storeProducts
            .map(p => p.globalProductId)
            .filter((id): id is string => !!id)
        );
        setSelectedIds(ids);
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const shouldSync = !isInitialSyncDone.current;
    if (shouldSync) isInitialSyncDone.current = true;
    fetchProducts(shouldSync);
  }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search, debouncedFilters.isGlobal, debouncedFilters.sort]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(selectedIds);
      products.forEach(p => allIds.add(p.id));
      setSelectedIds(allIds);
    } else {
      const remainingIds = new Set(selectedIds);
      products.forEach(p => remainingIds.delete(p.id));
      setSelectedIds(remainingIds);
    }
  };

  const handleImportProduct = async () => {
    let idsToImport: string[] = Array.from(selectedIds);
    setIsImportLoading(true);
    try {
      const response = await callApi(
        `${API_URL}${API_ENDPOINT.products}/import`,
        {
          method: 'POST',
          data: { globalProductIds: idsToImport }
        }
      );
      showToastNotification("success", response?.message || "Catalog synchronized successfully");
      setShowImportModal(false);
      navigate(RoutesLink.Products);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsImportLoading(false);
    }
  };

  const handleSelectItem = (item: IProduct, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(item.id);
    } else {
      newSelected.delete(item.id);
    }
    setSelectedIds(newSelected);
  };

  const columns: TableColumn<IProduct>[] = [
    {
      key: "image",
      header: "Product",
      render: (item) => (
        <TableItemIdentity
          image={item.media && item.media[0]?.url}
          name={item.name}
          fallbackText="No Img"
        />
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (item) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">{item.category?.name || '---'}</span>
          <span className="text-[10px] text-muted-foreground">{item.subCategory?.name || '---'}</span>
        </div>
      )
    },
    {
      key: "price",
      header: "Price",
      render: (item) => (
        <div className="font-semibold text-primary">
          {formatRand(item.price)}
        </div>
      )
    },
    {
      key: "productSource",
      header: "Product Source",
      render: () => (
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
          Admin Product
        </span>
      )
    }
  ];

  const getActions = (_record: IProduct): Action<IProduct>[] => [
    {
      text: "View",
      icon: <Eye className="w-4 h-4 cursor-pointer" />,
      onClick: (record) => navigate(RoutesLink.ViewProduct.replace(':id', record.id))
    }
  ];

  return (
    <Layout>
      <MainHeading
        title="Global Product Catalog"
        description="Choose products from the business catalog to offer in your store."
        showBack={true}
        onBack={() => navigate(RoutesLink.Products)}
        actionLabel="Save Sync"
        actionIcon={<CheckSquare className="w-4 h-4" />}
        onAction={() => setShowImportModal(true)}
        isActionDisabled={selectedIds.size === 0}
      />

      <div className="mt-6 flex flex-col gap-4">
        <TableFilters
          searchTerm={filters.search}
          onSearchChange={(val) => updateFilter({ search: val })}
          placeholder="Search items by name or ID..."
        >
          <CommonSelect
            value={filters.sort || "newest"}
            onValueChange={(val) => updateFilter({ sort: val })}
            options={[
              { value: "newest", label: "Newest First" },
              { value: "oldest", label: "Oldest First" },
            ]}
            triggerClassName="w-[180px] h-9 rounded-lg border-secondary/20 bg-secondary/5 font-semibold"
            placeholder="Sort By"
          />
        </TableFilters>

        <CommonTable
          columns={columns}
          data={products}
          loading={loading}
          showCheckbox={true}
          selectedItems={selectedIds}
          onSelectItem={(item, checked) => handleSelectItem(item, checked)}
          onSelectAll={handleSelectAll}
          isAllSelected={products.length > 0 && products.every(p => selectedIds.has(p.id))}
          emptyMessage="No catalog products available."
          actions={getActions}
        />

        <Pagination
          currentPage={filters.page || 1}
          totalPages={pageMetadata.totalPages}
          totalItems={pageMetadata.total}
          itemsPerPage={pageMetadata.limit}
          onPageChange={handlePageChange}
        />
      </div>

      <ConfirmationModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        onConfirm={handleImportProduct}
        title="Synchronize Catalog"
        message="Are you sure you want to synchronize your store catalog? Selected products will be added/kept, and unchecked products will be removed from your store."
        confirmText="Sync My Catalog"
        loading={isImportLoading}
        type="info"
        size="lg"
      />

      {selectedIds.size > 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="bg-foreground text-background px-6 py-3 rounded-full shadow-2xl flex items-center gap-6 border border-background/20">
            <div className="flex items-center gap-2 border-r border-background/20 pr-6">
              <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                {selectedIds.size}
              </span>
              <span className="text-sm font-medium">Products Selected</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 hover:text-primary transition-colors font-bold text-sm cursor-pointer"
              >
                <DownloadCloud className="w-4 h-4" />
                Sync Store Catalog
              </button>
              <button
                onClick={() => navigate(RoutesLink.Products)}
                className="flex items-center gap-2 hover:text-red-400 transition-colors font-bold text-sm cursor-pointer"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
